import { createAsyncThunk, createSlice, isPending, isRejected } from '@reduxjs/toolkit';
import axios from 'axios';

import { IBusiness } from 'app/shared/model/business.model';
import { serializeAxiosError } from 'app/shared/reducers/reducer.utils';

const initialState = {
  loading: false,
  errorMessage: null as string | null,
  businesses: [] as readonly IBusiness[],
  totalItems: 0,
};

export type AdminBusinessesState = Readonly<typeof initialState>;

const adminUrl = 'api/admin/business';

interface FetchParams {
  page: number;
  size: number;
  sort: string;
  /** Optional RSQL filter (e.g. a registration-date range). */
  /** Optional RSQL filter (date range AND/OR the free-text search clause), built on the client. */
  query?: string;
}

// Timora backend exposes the admin business list as an RSQL filter endpoint returning a
// GenericPaginationResponse ({ data, pagination }) — same contract as user-management.
export const getBusinesses = createAsyncThunk(
  'adminBusinesses/fetch_businesses',
  async ({ page, size, sort, query }: FetchParams, { signal }) => {
    const q = query ? `&q=${encodeURIComponent(query)}` : '';
    // Stable PK tiebreaker so rows don't reshuffle across edits when the primary sort has ties (see user reducer).
    const requestUrl = `${adminUrl}/filter?page=${page}&size=${size}&sort=${sort}&sort=id,desc${q}`;
    return axios.get<{ data: IBusiness[]; pagination: { totalItems: number } }>(requestUrl, { signal });
  },
  { serializeError: serializeAxiosError },
);

export const AdminBusinessesSlice = createSlice({
  name: 'adminBusinesses',
  initialState: initialState as AdminBusinessesState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getBusinesses.fulfilled, (state, action) => {
        state.loading = false;
        state.businesses = action.payload.data.data;
        state.totalItems = action.payload.data.pagination.totalItems;
      })
      .addMatcher(isPending(getBusinesses), state => {
        state.loading = true;
        state.errorMessage = null;
      })
      .addMatcher(isRejected(getBusinesses), (state, action) => {
        if (action.meta?.aborted) return; // superseded by a newer request — ignore
        state.loading = false;
        state.errorMessage = action.error.message!;
      });
  },
});

export const { reset } = AdminBusinessesSlice.actions;

export default AdminBusinessesSlice.reducer;
